import { Link } from "react-router-dom";
import { Sparkles, Moon, Sun, ChevronLeft, ChevronRight } from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { publicMediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const Logo = ({ to = "/" }) => {
  const { settings } = useAuth();
  return (
    <Link to={to} data-testid="brand-logo" className="flex items-center gap-2.5 group">
      {settings.logo_file_id ? (
        <img src={publicMediaUrl(settings.logo_file_id)} alt={settings.site_name} className="h-9 w-9 rounded-xl object-cover" />
      ) : (
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 shadow-lg shadow-violet-500/30 transition-transform group-hover:rotate-6">
          <Sparkles className="h-5 w-5 text-white" />
        </span>
      )}
      <span className="whitespace-nowrap font-display text-lg font-bold tracking-tight">{settings.site_name || "AI Creative Studio"}</span>
    </Link>
  );
};

export const ThemeToggle = () => {
  const { resolvedTheme, setTheme } = useTheme();
  return (
    <Button variant="ghost" size="icon" data-testid="theme-toggle" aria-label="Tema değiştir" onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}>
      {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
};

export const PageHeader = ({ title, desc, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h1 className="text-2xl font-bold sm:text-3xl" data-testid="page-title">{title}</h1>
      {desc && <p className="mt-1.5 text-sm text-muted-foreground sm:text-base">{desc}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const StatCard = ({ icon: Icon, label, value, hint, testid, accent }) => (
  <div className="glass-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {Icon && <span className={`grid h-9 w-9 place-items-center rounded-xl ${accent || "bg-primary/10 text-primary"}`}><Icon className="h-4 w-4" /></span>}
    </div>
    <div className="mt-3 font-display text-2xl font-bold sm:text-3xl">{value}</div>
    {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
  </div>
);

export const Pager = ({ page, pages, onChange }) =>
  pages > 1 ? (
    <div className="mt-6 flex items-center justify-center gap-3" data-testid="pager">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="pager-prev"><ChevronLeft className="h-4 w-4" /></Button>
      <span className="text-sm text-muted-foreground">{page} / {pages}</span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="pager-next"><ChevronRight className="h-4 w-4" /></Button>
    </div>
  ) : null;

export const Empty = ({ icon: Icon, title, desc, children }) => (
  <div className="glass-card flex flex-col items-center justify-center px-6 py-14 text-center" data-testid="empty-state">
    {Icon && <Icon className="mb-3 h-10 w-10 text-muted-foreground/60" />}
    <div className="font-display text-lg font-semibold">{title}</div>
    {desc && <p className="mt-1 max-w-md text-sm text-muted-foreground">{desc}</p>}
    {children && <div className="mt-5">{children}</div>}
  </div>
);

export const Spinner = () => (
  <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
);

export const SimpleTable = ({ cols, rows, testid, empty = "Kayıt bulunamadı." }) => (
  <div className="glass-card overflow-hidden" data-testid={testid}>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>{cols.map((c) => <th key={c.key} className="whitespace-nowrap px-4 py-3 font-semibold">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-muted-foreground">{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-border/60 transition-colors last:border-0 hover:bg-muted/30">
              {cols.map((c) => <td key={c.key} className="px-4 py-3 align-middle">{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
